import React, { useState } from 'react';
import { coffeeDispatches } from '../lib/api';
import { LevelBadge } from './badges';

export const EditDispatchModal = ({ dispatch, clientNames, onClose, onSaved }) => {
  const [form, setForm] = useState({
    client_name: dispatch.client_name || '',
    invoice_no: dispatch.invoice_no || '',
    notes: dispatch.notes || '',
    payment_status: dispatch.payment_status || 'Unpaid',
    amount_paid: dispatch.amount_paid ?? 0,
  });
  const [prices, setPrices] = useState((dispatch.items || []).map((it) => it.sale_price_per_kg ?? 0));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await coffeeDispatches.update(dispatch.id, {
        client_name: form.client_name.trim(),
        invoice_no: form.invoice_no.trim(),
        notes: form.notes,
        payment_status: form.payment_status,
        amount_paid: parseFloat(form.amount_paid) || 0,
        sale_prices: prices.map((p) => parseFloat(p) || 0),
      });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update dispatch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center shrink-0">
          <h2 className="text-xl font-bold text-gray-800">Edit Dispatch</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
              <input type="text" list="edit-client-names" value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                data-testid="edit-dispatch-client-input"
                className="w-full px-3 py-2 border rounded-lg" />
              <datalist id="edit-client-names">
                {clientNames.map((n) => <option key={n} value={n} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No.</label>
              <input type="text" value={form.invoice_no}
                onChange={(e) => setForm({ ...form, invoice_no: e.target.value })}
                data-testid="edit-dispatch-invoice-input"
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Sale Prices (₹/kg)</label>
            <div className="space-y-2">
              {(dispatch.items || []).map((it, idx) => (
                <div key={`${it.blend_name || it.category_id}-${it.roast_level}-${idx}`} className="flex items-center gap-2 bg-gray-50 border rounded-lg p-2">
                  <div className="flex-1 text-sm">
                    {it.components?.length ? (
                      <>
                        <span className="font-medium">{it.blend_name || 'Blend'}</span>{' '}
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700">BLEND</span>{' '}
                        <span className="text-gray-500">{it.quantity_kg} kg</span>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {it.components.map((c) => `${c.category_name} ${c.roast_level} ${c.quantity_kg}kg`).join(' + ')}
                        </p>
                      </>
                    ) : (
                      <>
                        <span className="font-medium">{it.category_name}</span>{' '}
                        <LevelBadge level={it.roast_level} />{' '}
                        <span className="text-gray-500">{it.quantity_kg} kg</span>
                      </>
                    )}
                  </div>
                  <input type="number" step="0.01" min="0" value={prices[idx]}
                    onChange={(e) => setPrices((prev) => prev.map((p, i) => (i === idx ? e.target.value : p)))}
                    data-testid={`edit-dispatch-price-${idx}`}
                    className="w-28 px-2 py-1.5 border rounded-lg text-sm text-right" />
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment</label>
              <div className="flex gap-2">
                {['Unpaid', 'Partial', 'Paid'].map((st) => (
                  <button key={st} type="button"
                    onClick={() => setForm({ ...form, payment_status: st })}
                    data-testid={`edit-payment-${st.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold border-2 transition ${
                      form.payment_status === st
                        ? (st === 'Paid' ? 'bg-emerald-500 text-white border-emerald-600' :
                           st === 'Partial' ? 'bg-amber-500 text-white border-amber-600' :
                           'bg-rose-500 text-white border-rose-600')
                        : 'bg-white text-gray-700 border-gray-200'
                    }`}>{st}</button>
                ))}
              </div>
            </div>
            {form.payment_status === 'Partial' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Received (₹)</label>
                <input type="number" step="0.01" min="0" value={form.amount_paid}
                  onChange={(e) => setForm({ ...form, amount_paid: e.target.value })}
                  data-testid="edit-amount-paid-input"
                  className="w-36 px-3 py-2 border rounded-lg" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows="2" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              data-testid="edit-dispatch-save-button"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
